/**
 * Explains Vault ownership, sharing, connected sources, and linked workspaces.
 *
 * The product combines several security boundaries that are not obvious from a
 * filesystem metaphor. Contextual help names those boundaries so users do not
 * assume a visible item is editable, exportable, or stored in DocReview.
 */
import {
  ArrowRight,
  Building2,
  Boxes,
  FolderInput,
  Link2,
  LockKeyhole,
  Share2,
} from 'lucide-react'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui'

export function VaultHelpDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>How Vault works</DialogTitle>
          <DialogDescription>
            Secure personal, workspace, and departmental file spaces for ordinary
            files and DocReview document intake.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-3">
          <HelpCard
            icon={Boxes}
            title="Choose the right Vault"
            body="Your Vault is automatic and private. Workspace Vault handles review intake. Departmental Vaults hold ordinary shared files."
          />
          <HelpCard
            icon={Link2}
            title="Enable the Workspace Vault"
            body="Workspace administrators turn Vault on in Settings. Incoming originals then appear as live, read-only views."
          />
          <HelpCard
            icon={Share2}
            title="Share the outcome"
            body="Invite UMD collaborators to selected files or folders without granting access to run AI in the source workspace."
          />
        </div>

        <section className="rounded-xl border bg-muted/30 p-4">
          <h3 className="font-medium">Typical workflow</h3>
          <div className="mt-3 flex flex-col gap-2 text-sm sm:flex-row sm:items-center">
            <FlowStep icon={FolderInput}>Connect a source or upload</FlowStep>
            <ArrowRight className="hidden h-4 w-4 shrink-0 text-muted-foreground sm:block" />
            <FlowStep icon={LockKeyhole}>Process in the workspace</FlowStep>
            <ArrowRight className="hidden h-4 w-4 shrink-0 text-muted-foreground sm:block" />
            <FlowStep icon={Share2}>Review in the workspace</FlowStep>
          </div>
        </section>

        <div className="grid gap-4 text-sm sm:grid-cols-2">
          <section>
            <h3 className="font-medium">Sources and sync</h3>
            <p className="mt-1 text-muted-foreground">
              Open a Workspace Vault&apos;s intake folder, choose Add source,
              select a provider folder, then choose whether to transfer current
              files or only future changes. Use the provider badge to inspect,
              sync, or disconnect it.
            </p>
          </section>
          <section>
            <h3 className="font-medium">Workspace boundary</h3>
            <p className="mt-1 text-muted-foreground">
              Vault sends documents into the configured review workflow. Analysis
              and review results stay in the source workspace.
            </p>
          </section>
          <section>
            <h3 className="font-medium">Workspace Vault settings</h3>
            <p className="mt-1 text-muted-foreground">
              A workspace administrator enables Vault, then chooses automatic
              agents, routing instructions, and who can access the Vault.
            </p>
          </section>
          <section>
            <h3 className="font-medium">Security boundary</h3>
            <p className="mt-1 text-muted-foreground">
              Vault access and workspace processing access are separate decisions.
              Sharing a read-only source never grants permission to run AI or modify
              its workspace record.
            </p>
          </section>
          <section>
            <h3 className="flex items-center gap-2 font-medium">
              <Building2 className="h-4 w-4" /> Departmental Vaults
            </h3>
            <p className="mt-1 text-muted-foreground">
              The university&apos;s directory structure creates Departmental
              Vaults and maps users through LDAP, Grouper, or an HR feed.
            </p>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function HelpCard({
  icon: Icon,
  title,
  body,
}: {
  icon: typeof Boxes
  title: string
  body: string
}) {
  return (
    <section className="rounded-xl border p-4">
      <Icon className="h-5 w-5 text-primary" />
      <h3 className="mt-3 text-sm font-medium">{title}</h3>
      <p className="mt-1 text-xs leading-5 text-muted-foreground">{body}</p>
    </section>
  )
}

function FlowStep({
  icon: Icon,
  children,
}: {
  icon: typeof Boxes
  children: string
}) {
  return (
    <span className="flex flex-1 items-center gap-2 rounded-lg bg-background px-3 py-2">
      <Icon className="h-4 w-4 shrink-0 text-primary" />
      {children}
    </span>
  )
}
